import flip from '@popperjs/core/lib/modifiers/flip';
import offset from '@popperjs/core/lib/modifiers/offset';
import preventOverflow from '@popperjs/core/lib/modifiers/preventOverflow';
import { createPopper } from '@popperjs/core/lib/popper-lite';
import log from 'lib/logger';
import { nextFrame } from '../helpers/timing';
import ApplicationController from './application_controller';

/**
 * @example
 * <div data-controller="popper" data-popper-tooltip-id-value="menu" data-action="open->popper#start close->popper#stop">
 *   <button>Open dropdown</button>
 *   <div id="menu">
 *     menu content
 *   </div>
 * </div>
 *
 * Customizing the placement. Default is `bottom`.
 * <div ... data-popper-placement-value="top"></div>
 *
 * Customizing the strategy. Default is `absolute`.
 * <div ... data-popper-strategy-value="fixed"></div>
 *
 * More popper options can be passed
 * <div ... data-popper-options-value="<%= { ...popperOptions }.to_json %>"></div>
 */

function sameWidthModifier({ enabled }) {
  return {
    name: 'sameWidth',
    enabled,
    phase: 'beforeWrite',
    requires: ['computeStyles'],
    fn: ({ state }) => {
      state.styles.popper.width = `${state.rects.reference.width}px`;
    },
    effect: ({ state }) => {
      state.elements.popper.style.width = `${state.elements.reference.offsetWidth}px`;
    },
  };
}

function responsiveModifier({ enabled, size }) {
  // Maps to bootstrap's responsive media sizes
  // Reference: https://github.com/twbs/bootstrap/blob/v4.6.2/scss/_variables.scss#L195
  const sizeMapping = {
    sm: 576,
    md: 768,
    lg: 992,
    xl: 1200,
  };

  if (size && !(size in sizeMapping)) {
    log.error(`Undefined size: ${size}`);
  }

  return {
    name: 'responsive',
    enabled,
    phase: 'afterWrite',
    fn: ({ state }) => {
      const { popper } = state.elements;
      // Remove popper styles so that we can override
      if (window.innerWidth < sizeMapping[size]) {
        popper.style.removeProperty('top');
        popper.style.removeProperty('bottom');
        popper.style.removeProperty('left');
        popper.style.removeProperty('right');
        popper.style.removeProperty('transform');
      }
    },
  };
}

export default class extends ApplicationController {
  static targets = ['tooltip', 'anchor'];
  static values = {
    tooltipId: String,
    strategy: { type: String, default: 'absolute' },
    placement: { type: String, default: 'bottom-end' },
    overflowBoundary: { type: String, default: 'clippingParents' },
    flipBoundary: { type: String, default: 'clippingParents' },
    fallbackPlacements: { type: Array, default: [] },
    sameWidth: { type: Boolean, default: false },
    startBreakpoint: String, // 'sm' | 'md' | 'lg' | 'xl' | undefined
    options: { type: Object, default: {} },
  };

  popperConfig = {
    strategy: this.strategyValue,
    placement: this.placementValue,
    modifiers: [
      preventOverflow,
      flip,
      offset,
      sameWidthModifier({ enabled: this.sameWidthValue }),
      responsiveModifier({ enabled: this.hasStartBreakpointValue, size: this.startBreakpointValue }),
      {
        name: 'flip',
        options: {
          boundary: this.flipBoundary,
          ...(this.fallbackPlacementsValue.length && { fallbackPlacements: this.fallbackPlacementsValue }),
        },
      },
      {
        name: 'offset',
        options: {
          offset: [0, 8],
        },
      },
      {
        name: 'preventOverflow',
        options: {
          boundary: this.overflowBoundary,
        },
      },
    ],
    ...this.optionsValue,
  };

  initialize() {
    if (!this.tooltip) return;

    this.popper = createPopper(this.anchor, this.tooltip, this.popperConfig);
  }

  // Force popper to create a new instance if it hasn't been created
  tooltipTargetConnected(target) {
    if (this.popper) return;
    this.popper = createPopper(this.anchor, target, this.popperConfig);
  }

  disconnect() {
    this.popper?.destroy();
    this.popper = undefined;
  }

  async start() {
    if (!this.popper || !this.tooltip) return;

    // Start watching for scroll changes
    this.popper.setOptions((options) => ({
      ...options,
      modifiers: [...options.modifiers, { name: 'eventListeners', enabled: true }],
    }));

    await nextFrame();
    this.update();
  }

  stop() {
    if (!this.popper || !this.tooltip) return;

    // Stop watching for scroll changes
    this.popper.setOptions((options) => ({
      ...options,
      modifiers: [...options.modifiers, { name: 'eventListeners', enabled: false }],
    }));
  }

  async update() {
    if (this.popper) {
      await this.popper.update();
    }
  }

  // Private

  get tooltip() {
    if (this.hasTooltipTarget) {
      return this.tooltipTarget;
    }

    return document.getElementById(this.tooltipIdValue);
  }

  get overflowBoundary() {
    if (this.overflowBoundaryValue === 'clippingParents') return this.overflowBoundaryValue;
    return document.getElementById(this.overflowBoundaryValue);
  }

  get flipBoundary() {
    if (this.flipBoundaryValue === 'clippingParents') return this.flipBoundaryValue;
    return document.getElementById(this.flipBoundaryValue);
  }

  get anchor() {
    if (this.hasAnchorTarget) {
      return this.anchorTarget;
    }

    return this.element;
  }
}
